// SEQ-232: "costs not entered" for the By month view, and the All projects row.
//
// WHY THIS EXISTS. Materials are a 50% budget of income and arrive with the
// claim. Wages and the other costs are manual rows entered in one batch around
// the 8th to 9th of the NEXT month. So a project-month that has income but no
// source='manual' row is not a 50% margin month, it is an unfinished one, and
// the current month is the clearest case (Tom Caelli, 28 October 2026). The
// rule is per project-month, not per calendar month: in July to September 1 to
// 4 projects a month had income and no manual costs.
//
// THIS IS A VIEW. It changes no stored figure, no formula and no RPC. A
// project-month with no costs may legitimately have no labour, which is why the
// marker is text and a switch can show the month again.
//
// XERO IS NEVER COUNTED. The read asks for source = 'manual' and nothing else.
// Xero rows are read only and are not part of these figures.
//
// FAIL SAFE. If the read errors, or comes back short of the row count the
// server reported, the check is "unavailable": nothing is marked and every
// figure stays exactly as it was. A capped or partial answer must never be
// read as "no costs entered" (production holds more manual rows than the
// PostgREST default cap of 1,000).

import type { PnlRollup } from "./rollup";
import { differenceRollup, sumRollups } from "./rollup";
import type { ProjectMonthRow, ProjectMonthTable } from "./projectMonthTable";
import { monthWindowTotalLabel } from "./projectMonthView";

export const COSTS_NOT_ENTERED_LABEL = "No costs entered";
export const COSTS_CHECK_FAILED_NOTE = "Could not check which months have costs entered";
/**
 * SEQ-240 audit B, row 15. The Totals view and the KPI cards count every project
 * month that has income, including months whose costs are not entered yet. The
 * By month view and the Profit Pareto grid leave those months out. Same page,
 * same dates, two profits: this sentence is what tells the reader why.
 */
export const TOTALS_COUNT_NO_COST_MONTHS_NOTE =
  "Totals count every project month with income, including months where costs are not entered yet. By month and the Profit Pareto grid leave those months out unless Show months without costs is on, so their profit can be lower.";
export const SHOW_NO_COST_MONTHS_LABEL = "Show months without costs";
export const SHOW_NO_COST_MONTHS_HINT =
  "A project month is marked No costs entered when it has income and no cost has been typed for that project and month. Costs go in around the 8th of the next month. Costs typed in a month with no income are never marked and always count.";
export const SHOW_NO_COST_MONTHS_STORAGE_KEY = "pnl-project-performance.by-month-show-no-cost-months.v1";
export const ALL_PROJECTS_LABEL = "All projects";

// ── The switch, saved per browser like the other By month choices ────────────

/** Default OFF: months without costs are left out until the user asks for them. */
export function parseSavedShowNoCostMonths(raw: string | null): boolean {
  if (!raw) return false;
  try {
    return JSON.parse(raw) === true;
  } catch {
    return false;
  }
}

export function serialiseShowNoCostMonths(show: boolean): string {
  return JSON.stringify(show === true);
}

/** The saved choice, read from this browser. Shared by every view that offers the switch, so it means the same thing everywhere. */
export function loadShowNoCostMonths(): boolean {
  let raw: string | null = null;
  try {
    raw = typeof localStorage === "undefined" ? null : localStorage.getItem(SHOW_NO_COST_MONTHS_STORAGE_KEY);
  } catch {
    raw = null;
  }
  return parseSavedShowNoCostMonths(raw);
}

export function saveShowNoCostMonths(show: boolean): void {
  try {
    if (typeof localStorage !== "undefined") localStorage.setItem(SHOW_NO_COST_MONTHS_STORAGE_KEY, serialiseShowNoCostMonths(show));
  } catch {
    // The choice just is not remembered.
  }
}

// ── The read: which project-months have a manual row ─────────────────────────

/** "projectId|YYYY-MM-01". The month is normalised, so a month_date part way through a month still lands on its month. */
export function projectMonthKey(projectId: string, dateOrMonth: string): string {
  return `${projectId}|${dateOrMonth.slice(0, 7)}-01`;
}

export interface ManualRowPage {
  rows: { project_id: string; month_date: string }[] | null;
  /** The server's exact count of matching rows across all pages. */
  count: number | null;
  error: string | null;
}

export type ManualCostCheck = { status: "ok"; keys: ReadonlySet<string> } | { status: "unavailable" };

export const MANUAL_ROWS_PAGE_SIZE = 1000;
/** A hard stop on paging, so a misbehaving server cannot loop the browser. 40,000 rows is far past 24 months of 100 projects. */
export const MANUAL_ROWS_MAX_PAGES = 40;

/**
 * Reads every manual (project, month) row in range, page by page, and returns
 * the set of keys that exist. Answers "unavailable" unless the pages add up to
 * the count the server reported, so a response cut short by a row cap, a
 * dropped page, an error or a missing count can never mark a real month
 * "no costs entered".
 *
 * `fetchPage` is given an inclusive [from, to] row range and must ask for an
 * exact count. A page shorter than asked is fine (the server may cap below our
 * page size); a page that is EMPTY before the count is reached is not.
 */
export async function loadManualCostKeys(
  fetchPage: (from: number, to: number) => Promise<ManualRowPage>,
  pageSize: number = MANUAL_ROWS_PAGE_SIZE,
  maxPages: number = MANUAL_ROWS_MAX_PAGES,
): Promise<ManualCostCheck> {
  const keys = new Set<string>();
  let fetched = 0;
  let expected: number | null = null;
  try {
    for (let page = 0; page < maxPages; page++) {
      const result = await fetchPage(fetched, fetched + pageSize - 1);
      if (result.error || !result.rows) return { status: "unavailable" };
      if (typeof result.count !== "number" || !Number.isFinite(result.count)) return { status: "unavailable" };
      if (expected === null) expected = result.count;
      else if (result.count !== expected) return { status: "unavailable" }; // rows changed under us
      for (const row of result.rows) {
        if (!row || typeof row.project_id !== "string" || typeof row.month_date !== "string") return { status: "unavailable" };
        keys.add(projectMonthKey(row.project_id, row.month_date));
      }
      fetched += result.rows.length;
      if (fetched >= expected) return fetched === expected ? { status: "ok", keys } : { status: "unavailable" };
      if (result.rows.length === 0) return { status: "unavailable" };
    }
  } catch {
    return { status: "unavailable" };
  }
  return { status: "unavailable" };
}

/** The first day of the month after `periodMonth` ('YYYY-MM-01'), for an exclusive upper bound. */
export function monthAfter(periodMonth: string): string {
  const year = Number(periodMonth.slice(0, 4));
  const month = Number(periodMonth.slice(5, 7));
  const nextYear = month === 12 ? year + 1 : year;
  const nextMonth = month === 12 ? 1 : month + 1;
  return `${String(nextYear).padStart(4, "0")}-${String(nextMonth).padStart(2, "0")}-01`;
}

// ── Marking, dropping and re-totalling ───────────────────────────────────────

/** True when a month cell has income. A cell with no income has nothing to be unfinished. */
export function hasIncome(rollup: PnlRollup | null | undefined): boolean {
  return !!rollup && rollup.revenue > 0;
}

/**
 * The check as far as it can be believed. The figures come from an RPC; the manual rows are read straight
 * from a table under row level security, and a narrower right there answers "0 rows" rather than an error
 * (the silent zero-row RLS shape this repo has met before). So an answer with NO manual rows at all, beside
 * project-months that do have income, is treated as unavailable: nothing is marked.
 */
export function trustedCheck(rowsByProject: Map<string, Map<string, PnlRollup>>, check: ManualCostCheck): ManualCostCheck {
  if (check.status !== "ok" || check.keys.size > 0) return check;
  for (const months of rowsByProject.values()) {
    for (const rollup of months.values()) if (hasIncome(rollup)) return { status: "unavailable" };
  }
  return check;
}

/** The "projectId|month" keys that have income and no manual row. Empty when the check is unavailable: nothing is marked. */
export function noCostKeys(rowsByProject: Map<string, Map<string, PnlRollup>>, rawCheck: ManualCostCheck): Set<string> {
  const flagged = new Set<string>();
  const check = trustedCheck(rowsByProject, rawCheck);
  if (check.status !== "ok") return flagged;
  for (const [projectId, months] of rowsByProject) {
    for (const [month, rollup] of months) {
      const key = projectMonthKey(projectId, month);
      if (hasIncome(rollup) && !check.keys.has(key)) flagged.add(key);
    }
  }
  return flagged;
}

/**
 * The months a column can be dropped for: every project that has a cell in the
 * month has it flagged, and at least one does. A month where even one project
 * has its costs in stays a column, with its unfinished cells left out.
 */
export function fullyUnfinishedMonths(
  months: string[],
  rowsByProject: Map<string, Map<string, PnlRollup>>,
  flagged: ReadonlySet<string>,
): Set<string> {
  const dropped = new Set<string>();
  for (const month of months) {
    let cells = 0;
    let unfinished = 0;
    for (const [projectId, perMonth] of rowsByProject) {
      if (!perMonth.has(month)) continue;
      cells++;
      if (flagged.has(projectMonthKey(projectId, month))) unfinished++;
    }
    if (cells > 0 && cells === unfinished) dropped.add(month);
  }
  return dropped;
}

/** The Total group's label: adds "with costs entered" when unfinished project-months were left out of it. */
export function costsTotalLabel(shownCount: number, windowNarrowed: boolean, leftOut: boolean): string {
  if (windowNarrowed && leftOut) return `Total, last ${shownCount} months with costs entered`;
  if (leftOut) return "Total, months with costs entered";
  return monthWindowTotalLabel(shownCount, windowNarrowed);
}

/**
 * Marks the table's rows and, unless `show`, leaves the unfinished project-months
 * out: their cells read blank (projectMonthCell honours `excluded`), and each
 * project's Total has them taken back out from `totalFromMonth` on, the first
 * month the rows' Total covers.
 */
export function applyCostsEntered(
  table: ProjectMonthTable,
  flagged: ReadonlySet<string>,
  show: boolean,
  totalFromMonth: string | null,
): { table: ProjectMonthTable; leftOutOfTotal: boolean } {
  if (flagged.size === 0) return { table, leftOutOfTotal: false };
  let leftOutOfTotal = false;
  const rows = table.rows.map((row): ProjectMonthRow => {
    const noCosts = new Set<string>();
    for (const month of row.months.keys()) {
      if (flagged.has(projectMonthKey(row.projectId, month))) noCosts.add(month);
    }
    if (noCosts.size === 0) return row;
    if (show) return { ...row, noCosts, excluded: new Set<string>() };
    let total = row.total;
    if (total && totalFromMonth) {
      const removed: PnlRollup[] = [];
      for (const month of noCosts) {
        const cell = row.months.get(month);
        if (cell && month >= totalFromMonth) removed.push(cell);
      }
      if (removed.length > 0) {
        total = differenceRollup(total, removed);
        leftOutOfTotal = true;
      }
    }
    return { ...row, noCosts, excluded: new Set(noCosts), total };
  });
  return { table: { ...table, rows }, leftOutOfTotal };
}

/**
 * The pinned All projects row: per month, the sum of every given project's cell
 * for it (excluded cells left out), percent metrics recomputed from the sums by
 * sumRollups. A month no project has a cell for is blank. Its Total is the sum
 * of the projects' Totals, which is how the page's other company figures are
 * built (project grain in, sum-then-divide out).
 */
export function buildAllProjectsRow(table: ProjectMonthTable, rows: ProjectMonthRow[], label: string = ALL_PROJECTS_LABEL): ProjectMonthRow {
  const months = new Map<string, PnlRollup>();
  for (const column of table.columns) {
    const cells: PnlRollup[] = [];
    for (const row of rows) {
      if (row.excluded?.has(column.periodMonth)) continue;
      const cell = row.months.get(column.periodMonth);
      if (cell) cells.push(cell);
    }
    if (cells.length > 0) months.set(column.periodMonth, sumRollups(cells));
  }
  const totals = rows.map((r) => r.total).filter((t): t is PnlRollup => t !== null);
  return {
    projectId: "all-projects",
    projectName: label,
    status: "",
    months,
    total: totals.length > 0 ? sumRollups(totals) : null,
  };
}
